/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useLocation, useParams, useRouter, useRoutes } from 'js/lib/useRouter';

import { Button } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import LoadingSpinner from 'bundles/grading/components/common/LoadingSpinner';
import type { GetUrlFn } from 'bundles/grading/components/staff-graded-assignments/SGAPageContext';
import type {
  LinearNavigationButtonAction,
  LinearNavigationButtonInfo,
  NavigationShortcutCallbacks,
  SGASubmitter,
} from 'bundles/grading/components/staff-graded-assignments/types/SGAGradingTypes';
import type { ReentryLogManager } from 'bundles/grading/utils/ReentryLogManager';
import { exitRoutes } from 'bundles/grading/utils/entryPointUtils';
import { useEventTracker } from 'bundles/grading/utils/hooks';

import _t from 'i18n!nls/grading';

type Props = {
  nextButtonInfo: LinearNavigationButtonInfo;
  disabled: boolean;
  currentSubmitterId: string;
  nextSubmitter: SGASubmitter | null;
  submittedGroup: SGASubmitter[];
  isTeamAssignment: boolean;
  getUrlCallback: GetUrlFn;
  shortcutCallbacks: NavigationShortcutCallbacks;
  autoFocus: boolean;
  reentryLog: ReentryLogManager;
};

const styles = {
  container: css`
    height: 100%;

    /*
     CDS uses a :focus-visible style to highlight a focused button. However, when navigating programmatically
     (through shortcuts) and using autofocus, the :focus-visible styles are ignored.
     That's why we are adding :focus styles to cover this case. These styles are the exact copy of the
     CDS :focus-visible styles so there is no change in the styling:
     https://github.com/webedx-spark/cds/blob/2fe93cb776034e0cfc1c0ead6bf2efaba66c18fe/packages/cds-core/src/ButtonBase/getButtonBaseCss.ts
    */
    &:focus {
      &::after {
        content: '';
        position: absolute;
        top: -2px;
        left: -2px;
        right: -2px;
        bottom: -2px;
        border-radius: 4px;
        box-shadow: 0 0 0 1px var(--cds-color-interactive-stroke-primary-focus),
          0 0 0 2px var(--cds-color-interactive-background-primary-hover-weak);
      }
    }
  `,
};

// "Next" button for SGA grade by submitter flow
const NextButtonSubmitterFlow = ({
  nextButtonInfo,
  disabled,
  currentSubmitterId,
  nextSubmitter,
  submittedGroup,
  isTeamAssignment,
  getUrlCallback,
  shortcutCallbacks,
  autoFocus,
  reentryLog,
}: Props) => {
  const router = useRouter();
  const routes = useRoutes();
  const routeParams = useParams();
  const routeLocation = useLocation();

  const trackNextButtonOnClickUsage = useEventTracker();

  // default next button action
  const nextButtonAction: LinearNavigationButtonAction = {
    title: _t('Next'),
  };

  const gradingGroupId = routeLocation.query?.groupId;

  const exitRoute = {
    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    name: exitRoutes[routes[routes.length - 1].name],
    params: routeParams,
    query: { ...(gradingGroupId ? { groupId: gradingGroupId } : {}) },
  };

  // if the nextButtonInfo is not null that means we're still in the current submission so use the details from the submissionManager
  if (nextButtonInfo !== null) {
    nextButtonAction.title = nextButtonInfo.title;
    nextButtonAction.buttonCallback = () =>
      router.push(getUrlCallback(currentSubmitterId, nextButtonInfo?.questionId, nextButtonInfo?.rubricId));
  }
  // if this is the last submitter of either the submitted/not-submitted groups, then show the "Done and close" title with an exit callback
  // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  else if (submittedGroup.length > 0 && currentSubmitterId === submittedGroup[submittedGroup.length - 1].id) {
    nextButtonAction.title = _t('Done and close');
    nextButtonAction.buttonCallback = () => {
      reentryLog.removeReentryLogItem();
      router.push(exitRoute);
    };
  }
  // if there is a valid next submitter, then show the next submitter for next button
  else if (nextSubmitter !== null) {
    nextButtonAction.title = isTeamAssignment ? _t('Next team') : _t('Next learner');
    nextButtonAction.buttonCallback = () => router.push(getUrlCallback(nextSubmitter?.id));
  }
  // failsafe, exit since there's no "next" criteria
  else {
    nextButtonAction.title = _t('Done and close');
    nextButtonAction.buttonCallback = () => {
      reentryLog.removeReentryLogItem();
      router.push(exitRoute);
    };
  }

  // eslint-disable-next-line no-param-reassign
  shortcutCallbacks.nextButtonCallback = disabled ? () => undefined : nextButtonAction.buttonCallback;

  // if button disabled, likely loading, use default "Next" language to avoid confusion
  return (
    <Button
      icon={disabled ? <LoadingSpinner /> : <ArrowNextIcon />}
      variant="primary"
      css={styles.container}
      fullWidth
      onClick={() => {
        if (nextButtonAction?.buttonCallback) {
          nextButtonAction.buttonCallback();
          trackNextButtonOnClickUsage({
            trackingName: 'quick_grader_linear_nav_next',
            action: 'click',
            trackingData: {},
          });
        }
      }}
      disabled={disabled}
      autoFocus={autoFocus}
    >
      {disabled && !nextButtonInfo ? _t('Next') : nextButtonAction.title}
    </Button>
  );
};

export default NextButtonSubmitterFlow;
